/** TanStack Query hooks for admin plugin + credential management. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";
import type { Plugin, Credential, KnowledgeSource, KnowledgeValidationResult } from "@/types";

// --- Admin Plugins ---

export function useAdminPlugins() {
  return useQuery<{ plugins: Plugin[] }>({
    queryKey: ["admin-plugins"],
    queryFn: () => api.get("/admin/reporting/plugins"),
  });
}

export function useUploadPlugin() {
  const qc = useQueryClient();
  return useMutation<Plugin, Error, Record<string, unknown>>({
    mutationFn: (body) => api.post("/admin/reporting/plugins", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plugins"] }),
  });
}

export function useTogglePluginStatus() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => api.put(`/admin/reporting/plugins/${id}/status`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plugins"] }),
  });
}

export function useUpdatePluginCode() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { pluginId: string; source: string }>({
    mutationFn: ({ pluginId, source }) => api.put(`/admin/reporting/plugins/${pluginId}/code`, { source }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plugins"] }),
  });
}

export function useDeletePlugin() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => api.del(`/admin/reporting/plugins/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plugins"] }),
  });
}

export function usePluginCode(pluginId: string) {
  return useQuery<{ pluginId: string; source: string }>({
    queryKey: ["plugin-code", pluginId],
    queryFn: () => api.get(`/admin/reporting/plugins/${pluginId}/code`),
    enabled: !!pluginId,
  });
}

export function useUpdatePluginMetadata() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { pluginId: string; description?: string; category?: string }>({
    mutationFn: ({ pluginId, ...body }) => api.put(`/admin/reporting/plugins/${pluginId}/metadata`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plugins"] }),
  });
}

// --- Admin Credentials ---

export function useAdminCredentials() {
  return useQuery<{ credentials: Credential[] }>({
    queryKey: ["admin-credentials"],
    queryFn: () => api.get("/admin/reporting/credentials"),
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation<Credential, Error, Record<string, unknown>>({
    mutationFn: (body) => api.post("/admin/reporting/credentials", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-credentials"] }),
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { credentialId: string } & Record<string, unknown>>({
    mutationFn: ({ credentialId, ...body }) => api.put(`/admin/reporting/credentials/${credentialId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-credentials"] }),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (id) => api.del(`/admin/reporting/credentials/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-credentials"] }),
  });
}

export function usePluginCredentials(pluginId: string) {
  return useQuery<{ items: { SK: string; credentialId: string }[] }>({
    queryKey: ["plugin-credentials", pluginId],
    queryFn: async () => {
      // Query PLUGIN#{pluginId}/CRED# items via a lightweight scan
      // The API doesn't have a dedicated endpoint, so we use the plugin detail
      // which includes credential assignments in the DynamoDB query
      const data = await api.get<{ items: { SK: string; credentialId: string }[] }>(
        `/admin/reporting/plugins/${pluginId}/credentials`
      );
      return data;
    },
    enabled: !!pluginId,
  });
}

export function useAssignCredential() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { pluginId: string; credentialId: string }>({
    mutationFn: ({ pluginId, credentialId }) =>
      api.post(`/admin/reporting/plugins/${pluginId}/credentials`, { credentialId }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["plugin-credentials", vars.pluginId] }),
  });
}

export function useUnassignCredential() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { pluginId: string; credentialId: string }>({
    mutationFn: ({ pluginId, credentialId }) =>
      api.del(`/admin/reporting/plugins/${pluginId}/credentials/${credentialId}`),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["plugin-credentials", vars.pluginId] }),
  });
}

// --- Gemini Models ---

export interface GeminiModelOption {
  value: string;
  label: string;
  detail: string;
}

/**
 * Fetch available Gemini models from the API. Enabled when a credential ID is provided,
 * indicating the node has a configured credential for making Gemini API calls.
 */
export function useGeminiModels(credentialId?: string) {
  return useQuery<{ models: GeminiModelOption[] }>({
    queryKey: ["gemini-models", credentialId ?? "default"],
    queryFn: () => api.get("/admin/reporting/gemini-models"),
    enabled: !!credentialId,
    staleTime: 5 * 60 * 1000, // Cache models for 5 minutes
  });
}

// --- Knowledge Sources ---

export function usePluginKnowledge(pluginId: string) {
  return useQuery<{ sources: KnowledgeSource[] }>({
    queryKey: ["plugin-knowledge", pluginId],
    queryFn: () => api.get(`/admin/reporting/plugins/${pluginId}/knowledge`),
    enabled: !!pluginId,
  });
}

export function useAddKnowledgeSource() {
  const qc = useQueryClient();
  return useMutation<KnowledgeSource, Error, { pluginId: string } & Record<string, unknown>>({
    mutationFn: ({ pluginId, ...body }) => api.post(`/admin/reporting/plugins/${pluginId}/knowledge`, body),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["plugin-knowledge", vars.pluginId] }),
  });
}

export function useUpdateKnowledgeSource() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { pluginId: string; sourceId: string } & Record<string, unknown>>({
    mutationFn: ({ pluginId, sourceId, ...body }) => api.put(`/admin/reporting/plugins/${pluginId}/knowledge/${sourceId}`, body),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["plugin-knowledge", vars.pluginId] }),
  });
}

export function useDeleteKnowledgeSource() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { pluginId: string; sourceId: string }>({
    mutationFn: ({ pluginId, sourceId }) => api.del(`/admin/reporting/plugins/${pluginId}/knowledge/${sourceId}`),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ["plugin-knowledge", vars.pluginId] }),
  });
}

export function useValidateKnowledgeSource() {
  return useMutation<KnowledgeValidationResult, Error, { pluginId: string; sourceType: string; driveFileId?: string; driveFolderId?: string }>({
    mutationFn: ({ pluginId, ...body }) => api.post(`/admin/reporting/plugins/${pluginId}/knowledge/validate`, body),
  });
}
